import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';

import { RequireAuth } from './guards/require-auth';
import { RequireRole } from './guards/require-role';

const lazy =
  <K extends string>(loader: () => Promise<Record<K, ComponentType>>, key: K) =>
  async () => {
    const m = await loader();
    return { Component: m[key] };
  };

export const protectedRoutes: RouteObject[] = [
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            path: '/',
            lazy: lazy(() => import('@/pages/dashboard/dashboard.page'), 'DashboardPage'),
          },

          {
            path: '/orders',
            lazy: lazy(() => import('@/pages/orders/orders-list.page'), 'OrdersListPage'),
          },
          {
            path: '/orders/:id',
            lazy: lazy(() => import('@/pages/orders/order-detail.page'), 'OrderDetailPage'),
          },
          {
            path: '/orders/:id/edit',
            lazy: lazy(() => import('@/pages/orders/order-edit.page'), 'OrderEditPage'),
          },

          {
            path: '/customers',
            lazy: lazy(() => import('@/pages/customers/customers-list.page'), 'CustomersListPage'),
          },
          {
            path: '/customers/:id',
            lazy: lazy(
              () => import('@/pages/customers/customer-detail.page'),
              'CustomerDetailPage',
            ),
          },

          {
            path: '/products',
            lazy: lazy(() => import('@/pages/products/products-list.page'), 'ProductsListPage'),
          },
          {
            path: '/products/:id/edit',
            lazy: lazy(() => import('@/pages/products/product-edit.page'), 'ProductEditPage'),
          },
          {
            path: '/products/bundles',
            lazy: lazy(() => import('@/pages/products/bundles-list.page'), 'BundlesListPage'),
          },

          {
            path: '/installations',
            lazy: lazy(
              () => import('@/pages/installations/installation-queue.page'),
              'InstallationQueuePage',
            ),
          },
          {
            path: '/installations/:id',
            lazy: lazy(
              () => import('@/pages/installations/installation-detail.page'),
              'InstallationDetailPage',
            ),
          },

          {
            path: '/devices',
            lazy: lazy(() => import('@/pages/devices/devices-list.page'), 'DevicesListPage'),
          },
          {
            path: '/devices/:id',
            lazy: lazy(() => import('@/pages/devices/device-detail.page'), 'DeviceDetailPage'),
          },

          {
            path: '/support',
            lazy: lazy(() => import('@/pages/support/tickets-list.page'), 'TicketsListPage'),
          },
          {
            path: '/support/:id',
            lazy: lazy(() => import('@/pages/support/ticket-detail.page'), 'TicketDetailPage'),
          },

          {
            path: '/subscriptions',
            lazy: lazy(
              () => import('@/pages/subscriptions/subscriptions-list.page'),
              'SubscriptionsListPage',
            ),
          },
          {
            path: '/payments',
            lazy: lazy(() => import('@/pages/payments/payments-list.page'), 'PaymentsListPage'),
          },

          {
            path: '/reports/orders',
            lazy: lazy(() => import('@/pages/reports/orders-report.page'), 'OrdersReportPage'),
          },
          {
            path: '/reports/device-count',
            lazy: lazy(
              () => import('@/pages/reports/device-count-report.page'),
              'DeviceCountReportPage',
            ),
          },
          {
            path: '/reports/device-activity',
            lazy: lazy(
              () => import('@/pages/reports/device-activity-report.page'),
              'DeviceActivityReportPage',
            ),
          },
          {
            path: '/reports/customer-usage',
            lazy: lazy(
              () => import('@/pages/reports/customer-usage-report.page'),
              'CustomerUsageReportPage',
            ),
          },
          {
            path: '/reports/subscription',
            lazy: lazy(
              () => import('@/pages/reports/subscription-report.page'),
              'SubscriptionReportPage',
            ),
          },

          {
            path: '/notifications',
            lazy: lazy(
              () => import('@/pages/notifications/notifications.page'),
              'NotificationsPage',
            ),
          },
          {
            path: '/activity-log',
            lazy: lazy(() => import('@/pages/activity-log/activity-log.page'), 'ActivityLogPage'),
          },

          {
            element: <RequireRole roles={['Admin']} />,
            children: [
              {
                path: '/configurations/tax',
                lazy: lazy(() => import('@/pages/configurations/tax-config.page'), 'TaxConfigPage'),
              },
              {
                path: '/configurations/discounts',
                lazy: lazy(() => import('@/pages/configurations/discounts.page'), 'DiscountsPage'),
              },
              {
                path: '/configurations/installation-fees',
                lazy: lazy(
                  () => import('@/pages/configurations/installation-fees.page'),
                  'InstallationFeesPage',
                ),
              },
              {
                path: '/configurations/warranty-periods',
                lazy: lazy(
                  () => import('@/pages/configurations/warranty-periods.page'),
                  'WarrantyPeriodsPage',
                ),
              },
              {
                path: '/admin-users',
                lazy: lazy(
                  () => import('@/pages/user-management/admin-users-list.page'),
                  'AdminUsersListPage',
                ),
              },
              {
                path: '/admin-users/:id/edit',
                lazy: lazy(
                  () => import('@/pages/user-management/admin-user-edit.page'),
                  'AdminUserEditPage',
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];
